
import { createContext, useContext, useId, useState, type ComponentProps, type ReactNode } from "react";
import { Check, Eye, EyeOff, ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

interface FieldCtx {
  id: string;
  describedBy?: string;
  invalid: boolean;
}
const Ctx = createContext<FieldCtx | null>(null);
const useField = () => useContext(Ctx);

interface FieldProps {
  label?: ReactNode;
  /** Right-aligned content on the label row, e.g. "Mot de passe oublié ?". */
  labelAside?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  children: ReactNode;
}

export function Field({ label, labelAside, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  return (
    <Ctx value={{ id, describedBy: [errId, hintId].filter(Boolean).join(" ") || undefined, invalid: !!error }}>
      <div className={className}>
        {(label || labelAside) && (
          <div className="mb-[7px] flex items-baseline justify-between gap-3">
            {label && (
              <label htmlFor={id} className="text-xs font-semibold text-ink">
                {label}
                {required && <span className="text-danger-500"> *</span>}
              </label>
            )}
            {labelAside && <div className="text-[11.5px] font-semibold">{labelAside}</div>}
          </div>
        )}
        {children}
        {error ? (
          <p id={errId} role="alert" className="mt-[5px] text-[11px] text-danger-500">
            {error}
          </p>
        ) : (
          hint && (
            <p id={hintId} className="mt-[5px] text-[11px] leading-relaxed text-muted">
              {hint}
            </p>
          )
        )}
      </div>
    </Ctx>
  );
}

/** Shared control look: 42px, radius 10, focus ring, red when invalid. */
export const controlClass = (invalid?: boolean) =>
  cn(
    "w-full rounded-[10px] border bg-white px-3.5 text-[13px] text-ink outline-none transition-[border-color,box-shadow]",
    "disabled:bg-canvas disabled:text-muted",
    invalid
      ? "border-danger-500 shadow-[0_0_0_0.5px_var(--color-danger-500)]"
      : "border-line-strong focus:border-primary-500 focus:shadow-[0_0_0_0.5px_var(--color-primary-500),0_0_0_3.5px_rgb(91_79_224/0.13)]",
  );

function useControlProps(props: { id?: string; "aria-invalid"?: ComponentProps<"input">["aria-invalid"] }) {
  const f = useField();
  const invalid = !!f?.invalid || props["aria-invalid"] === true;
  return { id: props.id ?? f?.id, "aria-describedby": f?.describedBy, "aria-invalid": invalid || undefined, invalid };
}

export function Input({ className, ...props }: ComponentProps<"input">) {
  const { invalid, ...a11y } = useControlProps(props);
  return <input {...props} {...a11y} className={cn(controlClass(invalid), "h-[42px]", className)} />;
}

export function Textarea({ className, rows = 3, ...props }: ComponentProps<"textarea">) {
  const { invalid, ...a11y } = useControlProps(props);
  return <textarea rows={rows} {...props} {...a11y} className={cn(controlClass(invalid), "py-[11px] leading-relaxed resize-y", className)} />;
}

export function Select({ className, children, ...props }: ComponentProps<"select">) {
  const { invalid, ...a11y } = useControlProps(props);
  return (
    <div className="relative">
      <select {...props} {...a11y} className={cn(controlClass(invalid), "h-[42px] appearance-none pr-9", className)}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 size-3.5 -translate-y-1/2 text-muted" strokeWidth={2.4} />
    </div>
  );
}

export function PasswordInput({ className, ...props }: Omit<ComponentProps<"input">, "type">) {
  const [shown, setShown] = useState(false);
  const { invalid, ...a11y } = useControlProps(props);
  return (
    <div className="relative">
      <input
        {...props}
        {...a11y}
        type={shown ? "text" : "password"}
        className={cn(controlClass(invalid), "h-11 pr-11", className)}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        className="absolute right-1.5 top-1/2 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted hover:bg-neutral hover:text-ink"
      >
        {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
}

interface CheckboxProps extends Omit<ComponentProps<"input">, "type"> {
  label: ReactNode;
}

export function Checkbox({ label, className, ...props }: CheckboxProps) {
  return (
    <label className={cn("inline-flex cursor-pointer items-start gap-2.5 text-[13px] leading-snug text-ink", className)}>
      <span className="relative mt-px inline-flex size-[18px] shrink-0">
        <input
          type="checkbox"
          {...props}
          className={cn(
            "peer size-full cursor-pointer appearance-none rounded-[5px] border-[1.5px] border-line-strong bg-white",
            "checked:border-primary-500 checked:bg-primary-500 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary-500/25",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        />
        <Check className="pointer-events-none absolute inset-0 m-auto size-[11px] text-white opacity-0 peer-checked:opacity-100" strokeWidth={3.4} />
      </span>
      <span>{label}</span>
    </label>
  );
}

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  size?: "sm" | "md";
}

export function Switch({ checked, onChange, label, disabled, size = "md" }: SwitchProps) {
  const track = size === "md" ? "h-[22px] w-[38px]" : "h-[15px] w-[26px]";
  const knob = size === "md" ? "size-[18px]" : "size-[11px]";
  const shift = size === "md" ? "translate-x-4" : "translate-x-[11px]";
  return (
    <label className={cn("inline-flex items-center gap-2.5 text-[13px] text-ink", disabled ? "opacity-50" : "cursor-pointer")}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex shrink-0 rounded-full p-0.5 transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary-500/25",
          track,
          checked ? "bg-primary-500" : "bg-line-strong",
        )}
      >
        <span className={cn("rounded-full bg-white shadow-sm transition-transform", knob, checked && shift)} />
      </button>
      {label}
    </label>
  );
}
